import { useUserSession } from '@solinkcorp/authentication';
import { getLocalTimeZone } from '@solinkcorp/utils';
import { CallhomeApi } from 'clients';
import { ScheduledActivityType } from 'clients/CallhomeApiTypes';
import { useMemo } from 'react';
import useFluxStore from 'src/hooks/useFluxStore';
import { LocationStore } from 'stores/LocationStore';
import useSWR from 'swr';
import { getCurrentMonthImageUsage } from 'utils/RecurrenceUtil';
import { OCCUR_ONCE } from 'vd-constants';

/**
 * This hook queries CH for all scheduled activities for the current customer and returns
 * a count of the number of images used by the activities for the current month. This number is
 * deducted from the remaining balance in order to determine a new activity (or on being edited)
 * will exceed the customer's image usage quota
 */
function useActiveSpotChecksImageUsage(
  shouldFetchAllActivities: boolean,
  activityToExcludeByid?: string,
) {
  const locationsById = useFluxStore(LocationStore, (store) => store.getLocationsById());

  const { accessToken } = useUserSession();

  const { data: scheduledActivities } = useSWR(
    shouldFetchAllActivities ? 'all-scheduled-activities' : null,
    () => {
      return CallhomeApi(accessToken).listRecurringActivities();
    },
    {
      revalidateOnFocus: false,
      errorRetryCount: 1,
      fallbackData: [],
    },
  );

  return useMemo(() => {
    if (!scheduledActivities.length) {
      return 0;
    }

    const activeScheduledActivities = scheduledActivities.filter((activity) => {
      if (
        activity.reason !== ScheduledActivityType.SCHEDULED_AI_CHAT ||
        (activityToExcludeByid && activity.id === activityToExcludeByid)
      ) {
        return false;
      } else if (activity.reoccurringCount === OCCUR_ONCE && activity.startsAt < Date.now()) {
        /** activity has occured once and has expired, don't count towards current month's image usage */
        return false;
      } else if (activity.endsAt && activity.endsAt < Date.now()) {
        /** filter out schedules with expired endsAt from count */
        return false;
      }

      return true;
    });

    return activeScheduledActivities.reduce((imagesUsed: number, activity) => {
      const location = activity?.siteId && locationsById?.[activity.siteId];

      return (
        imagesUsed +
        getCurrentMonthImageUsage(
          activity,
          activity.cameraIds.length,
          location?.timezone ?? getLocalTimeZone(),
        )
      );
    }, 0);
  }, [scheduledActivities, locationsById, activityToExcludeByid]);
}

export default useActiveSpotChecksImageUsage;
